import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { StompSubscription } from '@stomp/stompjs';
import { WebRecordedCommand, WebRecordedStep } from '../../Models/web-recorded-command-model';
import { RecorderSessionService } from '../../Services/recorder-session.service';
import { RecordedStepsService } from '../../Services/recorded-steps.service';
import { WebsocketService } from '../../Services/websocket.service';
import { NamePromptService } from '../../Services/name-prompt.service';
import { ElementDetailsService } from '../../Services/element-details.service';
import { RestService } from '../../Services/rest.service';
import { StepServiceService } from '../../Services/step-service.service';
import { CTVService } from '../../Services/ctv.service';

// Commands with no real DOM element to inspect — a single target string
// (a URL, or a win_ser_N tab handle) instead. Same rendering as "open":
// no gear/element-details icon, target shown as plain text.
const NO_ELEMENT_COMMANDS = new Set(['open', 'selectWindow', 'close']);

// Every right-click command CodeTestIDE's own contextMenu handler
// (content/recorder-handlers.js) can record, needing only a target element.
// "Delayed" ones just arm a flag its existing hover/click handlers already
// check, completing on whatever the user does next on the page.
const CONTEXT_MENU_COMMANDS: { cmd: string; label: string; group: 'immediate' | 'delayed' }[] = [
  { cmd: 'verifyText', label: 'Verify Text', group: 'immediate' },
  { cmd: 'verifyTextContains', label: 'Verify Text Contains', group: 'immediate' },
  { cmd: 'verifyDropdownSelect', label: 'Verify Select Dropdown Text', group: 'immediate' },
  { cmd: 'verifyelementchecked', label: 'Verify Element Checked', group: 'immediate' },
  { cmd: 'verifyelementunchecked', label: 'Verify Element UnChecked', group: 'immediate' },
  { cmd: 'assertText', label: 'Assert Text', group: 'immediate' },
  { cmd: 'assertSelectDropdownText', label: 'Assert Select Dropdown Text', group: 'immediate' },
  { cmd: 'assertelementchecked', label: 'Assert Element Checked', group: 'immediate' },
  { cmd: 'assertelementunchecked', label: 'Assert Element UnChecked', group: 'immediate' },
  { cmd: 'assertCompareImage', label: 'Assert Compare Image', group: 'immediate' },
  { cmd: 'storeText', label: 'Store Text', group: 'immediate' },
  { cmd: 'storeValue', label: 'Store Value', group: 'immediate' },
  { cmd: 'waitForElementPresent', label: 'Wait For Element Present', group: 'immediate' },
  { cmd: 'mouseover', label: 'Mouse Over', group: 'immediate' },
  { cmd: 'rightclick', label: 'Right Click', group: 'immediate' },
  { cmd: 'dropdownSelect', label: 'Dropdown Select', group: 'immediate' },
  { cmd: 'textByKeys', label: 'Text By Keys', group: 'immediate' },
  { cmd: 'findRowByValue', label: 'Find Row By Value', group: 'immediate' },
  { cmd: 'ifElementPresent', label: 'If Element Present', group: 'immediate' },

  { cmd: 'verifyTextInRegion', label: 'Verify Text In Region', group: 'delayed' },
  { cmd: 'verifyTextContainsInRegion', label: 'Verify Text Contains In Region', group: 'delayed' },
  { cmd: 'delayedVerify', label: 'Verify Text', group: 'delayed' },
  { cmd: 'delayedVerifyDropdownSelect', label: 'Verify Select Dropdown Text', group: 'delayed' },
  { cmd: 'delayedVerifyElementChecked', label: 'Verify Element Checked', group: 'delayed' },
  { cmd: 'delayedVerifyElementUnChecked', label: 'Verify Element UnChecked', group: 'delayed' },
  { cmd: 'delayedVerifyCompareImage', label: 'Verify Compare Image', group: 'delayed' },
  { cmd: 'delayedVerifyCompareImageDropdown', label: 'Verify Compare Image Locked Frame', group: 'delayed' },
  { cmd: 'delayedAssert', label: 'Assert Text', group: 'delayed' },
  { cmd: 'delayedAssertDropdownSelect', label: 'Assert Select Dropdown Text', group: 'delayed' },
  { cmd: 'delayedAssertElementChecked', label: 'Assert Element Checked', group: 'delayed' },
  { cmd: 'delayedAssertElementUnChecked', label: 'Assert Element UnChecked', group: 'delayed' },
  { cmd: 'delayedAssertCompareImage', label: 'Assert Compare Image', group: 'delayed' },
  { cmd: 'delayedAssertCompareImageDropdown', label: 'Assert Compare Image Locked Frame', group: 'delayed' },
  { cmd: 'delayedStore', label: 'Store Text', group: 'delayed' },
  { cmd: 'delayedStoringVals', label: 'Store Value', group: 'delayed' },
  { cmd: 'delayedWaitForElement', label: 'Wait For Element Present', group: 'delayed' },
  { cmd: 'delayedMouseOver', label: 'Mouse Over', group: 'delayed' },
  { cmd: 'delayedMultipleMouseOver', label: 'Multiple Mouse Over', group: 'delayed' },
  { cmd: 'delayedRightClick', label: 'Right Click', group: 'delayed' },
  { cmd: 'delayedDragAndDropToOffset', label: 'Drag And Drop To Offset', group: 'delayed' },
  // Literal id has spaces — matches background.js's own menu item id exactly.
  { cmd: 'drag and drop with coordinates', label: 'Drag And Drop With Coordinates', group: 'delayed' },
  { cmd: 'delayedDropTarget', label: 'Drop Target', group: 'delayed' },
  { cmd: 'delayedCanvasClick', label: 'Canvas Click', group: 'delayed' },
  { cmd: 'delayedCanvasRightClick', label: 'Canvas Right Click', group: 'delayed' },
  { cmd: 'delayedCanvasDoubleClick', label: 'Canvas Double Click', group: 'delayed' },
  { cmd: 'delayedCanvasMultipleClicks', label: 'Canvas Multiple Clicks', group: 'delayed' }
];

@Component({
  selector: 'ze-web-recorded-steps',
  templateUrl: './web-recorded-steps.component.html',
  styleUrls: ['./web-recorded-steps.component.scss']
})
export class WebRecordedStepsComponent implements OnInit, OnDestroy {

  @Input() isRecording = false;

  private subscription: StompSubscription | null = null;

  openMenuStepId: number | null = null;

  // True the instant a right-click fires (cdp-bootstrap.js's
  // __ataContextMenuOpened) — waiting to be told which command was picked.
  contextMenuArmed = false;
  contextMenuCommandsImmediate = CONTEXT_MENU_COMMANDS.filter(c => c.group === 'immediate');
  contextMenuCommandsDelayed = CONTEXT_MENU_COMMANDS.filter(c => c.group === 'delayed');
  contextMenuTab: 'immediate' | 'delayed' = 'immediate';
  private sendingContextMenuCommand = false;

  toastType: 'success' | 'error' = 'success';
  toastMessage: string | null = null;
  private toastTimer: any = null;

  // Mirrors CodeTestIDE extension's own "English Description" toggle
  // (#plain-english-toggle in its command grid) — default on, same as there.
  showEnglishDescription = true;

  constructor(
    public recorderSession: RecorderSessionService,
    public steps: RecordedStepsService,
    private websocketService: WebsocketService,
    private namePrompt: NamePromptService,
    private elementDetails: ElementDetailsService,
    private restService: RestService,
    private stepService: StepServiceService,
    private ctvService: CTVService
  ) {}

  // Gear icon — shows every locator strategy captured for this element.
  onOpenElementDetails(command: WebRecordedCommand, event: MouseEvent) {
    event.stopPropagation();
    this.elementDetails.open(command.targetCandidates || []);
  }

  isTargetOnlyCommand(command: string): boolean {
    return NO_ELEMENT_COMMANDS.has(command);
  }

  // ── Editable value column (matches the extension's own grid, where the
  //    value cell is editable, not just a display) ──────────────────────
  editingCommandId: number | null = null;
  editingValue = '';

  onStartEditValue(command: WebRecordedCommand, event: MouseEvent) {
    if (command.isPassword) return;
    event.stopPropagation();
    this.editingCommandId = command.id;
    this.editingValue = command.value || '';
  }

  onSaveEditValue(command: WebRecordedCommand) {
    if (this.editingCommandId !== command.id) return;
    if (this.selectedStep) {
      this.steps.updateCommandValue(this.selectedStep, command.id, this.editingValue);
    }
    this.editingCommandId = null;
  }

  onCancelEditValue() {
    this.editingCommandId = null;
  }

  ngOnInit() {
    this.websocketService._connect();
    this.websocketService.onConnection().subscribe(() => this.subscribeToCommands());
    this.loadSavedStepsIntoSession();
  }

  // Pulls every backend-saved step into the session step list, fully
  // populated. Idempotent (loadSavedStep skips already-loaded ids), so
  // safe to re-run after a refresh.
  private loadSavedStepsIntoSession(): void {
    this.stepService.getItemsdb2().subscribe({
      next: (res: any) => {
        // Only steps the Web Recorder itself saved (its "Save Steps"
        // checkbox) — NOT every step ever saved to Canvas by any means.
        const savedSteps: any[] = (res.data || []).filter((m: any) => !!m && m.source === 'web_recorder');
        savedSteps.forEach(model => {
          this.ctvService.getItemsByStepdb2(model['_id']).subscribe({
            next: (ctvRes: any) => {
              const commands = ((ctvRes && ctvRes.data) || []).filter(Boolean);
              this.steps.loadSavedStep({ _id: model['_id'], name: model.name }, commands);
            },
            error: () => { /* one step's commands failing to load shouldn't block the rest */ }
          });
        });
      },
      error: () => { /* saved steps just won't appear pre-loaded — not fatal */ }
    });
  }

  ngOnDestroy() {
    if (this.subscription) this.subscription.unsubscribe();
  }

  private subscribeToCommands() {
    if (!this.recorderSession.sessionId) return;
    if (this.subscription) this.subscription.unsubscribe();
    const topic = '/message/db1/recorder/command/' + this.recorderSession.sessionId;
    this.subscription = this.websocketService.stompClient.subscribe(topic, (message: any) => {
      const body = JSON.parse(message.body);
      const raw = body.data || body;
      if (raw.__ataContextMenuOpened) {
        this.contextMenuArmed = true;
        this.contextMenuTab = 'immediate';
        return;
      }
      this.steps.appendCommand(raw);
    });
  }

  // ── Right-click ("XPeer.ai") context-menu commands ──────────────────────

  onPickContextMenuCommand(cmd: string) {
    if (this.sendingContextMenuCommand) return;
    this.sendingContextMenuCommand = true;
    const projectId = localStorage.getItem('projectIdDb2') || '';
    this.restService.post('recorderContextMenuCommand', { projectId, cmd }).subscribe(
      () => { this.sendingContextMenuCommand = false; this.contextMenuArmed = false; },
      () => { this.sendingContextMenuCommand = false; this.contextMenuArmed = false; }
    );
  }

  onCancelContextMenu() {
    this.contextMenuArmed = false;
  }

  // ── List mode ────────────────────────────────────────────────────────────

  selectStep(step: WebRecordedStep) {
    this.steps.selectStep(step);
  }

  private showToast(type: 'success' | 'error', message: string): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastType = type;
    this.toastMessage = message;
    this.toastTimer = setTimeout(() => { this.toastMessage = null; this.toastTimer = null; }, 3500);
  }

  isSelected(step: WebRecordedStep): boolean {
    return !!this.steps.selectedStep && this.steps.selectedStep.id === step.id;
  }

  isMenuOpen(step: WebRecordedStep): boolean {
    return this.openMenuStepId === step.id;
  }

  toggleMenu(step: WebRecordedStep, event: MouseEvent) {
    event.stopPropagation();
    this.openMenuStepId = this.isMenuOpen(step) ? null : step.id;
  }

  @HostListener('document:click')
  closeMenu() {
    this.openMenuStepId = null;
    this.commandContextMenu = null;
  }

  onMenuDelete(step: WebRecordedStep) {
    this.steps.deleteStep(step);
    this.openMenuStepId = null;
  }

  onMenuCopy(step: WebRecordedStep) {
    this.steps.copyStep(step);
    this.openMenuStepId = null;
  }

  async onMenuRename(step: WebRecordedStep) {
    this.openMenuStepId = null;
    const name = await this.namePrompt.open('Rename Step', step.name);
    if (name) this.steps.renameStep(step, name);
  }

  // ── Detail mode (selected step's commands — toolbar lives in
  //    ze-simulator-toolbar, which spans full width above this sidebar) ────

  get selectedStep(): WebRecordedStep | null {
    return this.steps.selectedStep;
  }

  onRemoveCommand(commandId: number) {
    if (!this.selectedStep) return;
    this.steps.removeCommand(this.selectedStep, commandId);
  }

  // Right-click menu on a command row — Delete/Delete All/Copy/Paste,
  // matching the reference recorder's own per-command context menu.
  commandContextMenu: { commandId: number; x: number; y: number } | null = null;

  openCommandContextMenu(commandId: number, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.commandContextMenu = { commandId, x: event.clientX, y: event.clientY };
  }

  closeCommandContextMenu() {
    this.commandContextMenu = null;
  }

  onCommandMenuDelete() {
    if (this.selectedStep && this.commandContextMenu) {
      this.steps.removeCommand(this.selectedStep, this.commandContextMenu.commandId);
    }
    this.closeCommandContextMenu();
  }

  onCommandMenuDeleteAll() {
    if (this.selectedStep) this.steps.removeAllCommands(this.selectedStep);
    this.closeCommandContextMenu();
  }

  onCommandMenuCopy() {
    if (this.selectedStep && this.commandContextMenu) {
      const commandId = this.commandContextMenu.commandId;
      const command = this.selectedStep.commands.find(c => c.id === commandId);
      if (command) this.steps.copyCommand(command);
    }
    this.closeCommandContextMenu();
  }

  onCommandMenuPaste() {
    if (this.selectedStep) this.steps.pasteCommand(this.selectedStep);
    this.closeCommandContextMenu();
  }
}
